/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Hyunsu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Mon Nov 04 2024
 **/

import { useCallback, useEffect, useRef, useState } from "react";
import { useTheme } from "@emotion/react";
import { throttle } from "lodash-es";
import styled from "@emotion/styled";

import { Button, Icon, Row } from "@/frameworks/designStuff/components";
import { Maybe } from "@/frameworks/libraries/components";
import { _T } from "@rfice/language";
import { v4 } from "uuid";

type AudioType = HTMLAudioElement & { setSinkId: (deviceId: string) => Promise<void> };

type SpeakerVisualizer = {
	id?: string;
	isDisabled?: boolean;
};

export default function SpeakerVisualizer({ id, isDisabled = false }: SpeakerVisualizer) {
	const audioRef = useRef<AudioType>(null);
	const audioContext = useRef<AudioContext>(new AudioContext());
	const gainNode = useRef<GainNode | null>(null);
	const analyserNode = useRef<AnalyserNode | null>(null);
	const dataArray = useRef<Uint8Array | null>(null);
	const intervalID = useRef(0);
	const theme = useTheme();
	const [gainValue, setGainValue] = useState(0);
	const [active, setActive] = useState(false);
	// const [device_id, set_device_id] = useState<string | undefined>(id);
	const sourceNode = useRef<MediaElementAudioSourceNode | null>(null);

	useEffect(() => {
		if (!audioRef.current) return;
		audioRef.current.addEventListener("play", onPlay);
		audioRef.current.addEventListener("pause", onPause);
	}, []);

	const onPause = () => {
		if (gainNode.current) {
			setActive(false);
			gainNode.current.gain.value = 0;
			setGainValue(0);
			cancelAnimationFrame(intervalID.current);
		}
	};

	const onPlay = useCallback(() => {
		try {
			if (!audioContext.current || !audioRef.current) return;

			// TODO 그리는 영역 개선
			if (!sourceNode.current) {
				sourceNode.current = audioContext.current.createMediaElementSource(
					audioRef.current as HTMLMediaElement
				);
			}

			gainNode.current = audioContext.current.createGain();
			analyserNode.current = audioContext.current.createAnalyser();
			analyserNode.current.fftSize = 32;
			dataArray.current = new Uint8Array(analyserNode.current.frequencyBinCount);
			gainNode.current.gain.value = 0.5;

			sourceNode.current.connect(gainNode.current!);
			gainNode.current.connect(analyserNode.current!);
			analyserNode.current.connect(audioContext.current.destination);

			draw();
		} catch {
			// @ignore
		}
	}, [id]);

	const draw = throttle(() => {
		intervalID.current = requestAnimationFrame(draw);

		if (analyserNode.current && dataArray.current) {
			analyserNode.current.getByteFrequencyData(dataArray.current);

			const newGainValue =
				(dataArray.current.reduce((acc, volume) => acc + volume, 0) /
					analyserNode.current.frequencyBinCount /
					255) *
				100;

			setGainValue(newGainValue);
		}
	}, 200);

	const handlePlay = useCallback(async () => {
		if (!audioRef.current || !id) return;

		if (active) {
			setActive(false);
			audioRef.current.pause();
			audioRef.current.currentTime = 0;
		} else {
			console.debug("#SpeakerVisualizer# Play", id);
			await audioRef.current
				.setSinkId(id)
				.then(() => {
					if (!audioRef.current) return;
					setActive(true);
					audioRef.current.play();
				})
				.catch((error) => {
					console.debug(error);
				});
		}
	}, [id, active]);

	return (
		<>
			<StyledVisualizer gap={8} horizonAlign="left" verticalAlign="center">
				<Icon
					name="SpeakerOn24"
					size={16}
					fill={active ? theme.colors.accent_40_100 : theme.colors.neutral_80_100}
				/>
				<Row.ul gap={4} horizonAlign="left">
					{Array.from({ length: 10 }, (_, i) => i + 1).map((item) => (
						<Gauge
							isDisabled={isDisabled}
							key={v4()}
							active={item <= Math.round((gainValue - 1) % 10) + 1}
						/>
					))}
				</Row.ul>
				<Maybe test={Boolean(id)}>
					<Button
						css={{ position: "absolute", right: 12 }}
						size="mini"
						shape="round"
						disabled={isDisabled}
						filled="primary"
						onClick={handlePlay}
					>
						{_T("테스트")}
					</Button>
				</Maybe>
			</StyledVisualizer>
			<Audio ref={audioRef} id="audio-source" src={"audio/speaker-test.mp3"} />
		</>
	);
}

const Audio = styled.audio``;

const Gauge = styled(Row.li)<{ active: boolean; isDisabled?: boolean }>`
	width: 6px;
	height: 10px;
	border-radius: 20px;
	background: ${({ theme, active, isDisabled }) =>
		`${isDisabled ? theme.colors.neutral_30_100 : active ? theme.colors.accent_40_100 : theme.colors.neutral_70_100}`};
	transition: all 0.1 ease-in-out;
`;

const StyledVisualizer = styled(Row.div)`
	position: relative;
	padding: 8px 12px;
	display: flex;
	width: 100%;
	height: 40px;
	overflow: hidden;
	background: ${({ theme }) => theme.colors.neutral_50_8};
	align-items: center;
`;
